import { useCallback, useEffect, useRef, useState } from 'react';

const VIOLATION_LIMIT = 3;

export type ViolationType =
  | 'tabSwitch'
  | 'windowBlur'
  | 'fullscreenExit'
  | 'copyPaste'
  | 'rightClick';

export interface UseExamSecurityOptions {
  /** Called when any violation occurs (use to show warning modal). Receives current violation count. */
  onViolation?: (violationCount: number, type: ViolationType) => void;
  /** Called when violation count reaches or exceeds the limit (e.g. auto-submit exam). */
  onViolationLimitExceeded?: () => void;
  /** Maximum violations before triggering limit exceeded. Default: 3 */
  violationLimit?: number;
  /** If true, security listeners are active. Default: true */
  enabled?: boolean;
}

export interface UseExamSecurityReturn {
  /** Current number of violations. */
  violations: number;
  /** Maximum allowed violations before auto-submit. */
  violationLimit: number;
  /** Reset violation count to 0. */
  resetViolations: () => void;
}

/**
 * Hook to enforce exam security: detects tab switch, window blur, fullscreen exit,
 * copy/paste, and right-click. Tracks violation count; when it exceeds the limit,
 * calls onViolationLimitExceeded (e.g. auto-submit exam).
 */
export function useExamSecurity({
  onViolation,
  onViolationLimitExceeded,
  violationLimit = VIOLATION_LIMIT,
  enabled = true,
}: UseExamSecurityOptions = {}): UseExamSecurityReturn {
  const [violations, setViolations] = useState(0);
  const onViolationRef = useRef(onViolation);
  const onLimitExceededRef = useRef(onViolationLimitExceeded);

  onViolationRef.current = onViolation;
  onLimitExceededRef.current = onViolationLimitExceeded;

  const lastViolationTimeRef = useRef(0);
  const COOLDOWN_MS = 2000;

  const handleViolation = useCallback(
    (type: ViolationType) => {
      const now = Date.now();
      if (now - lastViolationTimeRef.current < COOLDOWN_MS) return;
      lastViolationTimeRef.current = now;

      setViolations((prev) => {
        const next = prev + 1;
        onViolationRef.current?.(next, type);
        if (next >= violationLimit) {
          onLimitExceededRef.current?.();
        }
        return next;
      });
    },
    [violationLimit]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        handleViolation('tabSwitch');
      }
    };

    const handleBlur = () => {
      handleViolation('windowBlur');
    };

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        handleViolation('fullscreenExit');
      }
    };

    const handleCopyPaste = (e: Event) => {
      e.preventDefault();
      handleViolation('copyPaste');
    };

    const handleRightClick = (e: Event) => {
      e.preventDefault();
      handleViolation('rightClick');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('copy', handleCopyPaste);
    document.addEventListener('paste', handleCopyPaste);
    document.addEventListener('cut', handleCopyPaste);
    document.addEventListener('contextmenu', handleRightClick);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('copy', handleCopyPaste);
      document.removeEventListener('paste', handleCopyPaste);
      document.removeEventListener('cut', handleCopyPaste);
      document.removeEventListener('contextmenu', handleRightClick);
    };
  }, [enabled, handleViolation]);

  const resetViolations = useCallback(() => setViolations(0), []);

  return {
    violations,
    violationLimit,
    resetViolations,
  };
}

export default useExamSecurity;
